import { get } from 'ravenjs/utils/lodash';

import { extractErrorMessage } from 'utils/errors';
import { createReducer } from 'utils/reducer';

import * as acaActions from './action';

const createOperationStatus = () => ({ isLoading: false, error: null });

const INITIAL_STATE = {
    expertConsent: {
        companyId: null,
        byYear: {},
        status: {
            fetch: createOperationStatus(),
            create: createOperationStatus(),
            update: createOperationStatus(),
        },
    },
};

const updateOperationStatus = (state, operation, status) => ({
    ...state,
    expertConsent: {
        ...state.expertConsent,
        status: {
            ...state.expertConsent.status,
            [operation]: {
                ...state.expertConsent.status[operation],
                ...status,
            },
        },
    },
});

const resetCompanyConsents = (expertConsent, companyId) => ({
    ...expertConsent,
    companyId,
    byYear: expertConsent.companyId === companyId ? expertConsent.byYear : {},
});

const handleFetchSuccess = (state, action) => {
    const { companyId, years = [] } = action.meta || {};
    const consentState = state.expertConsent;
    const byYear = consentState.companyId === companyId ? { ...consentState.byYear } : {};

    years.forEach(year => {
        const yearKey = String(year);
        byYear[yearKey] = action.payload.filter(row => String(row.year) === yearKey);
    });

    return updateOperationStatus(
        {
            ...state,
            expertConsent: {
                ...consentState,
                companyId,
                byYear,
            },
        },
        'fetch',
        { isLoading: false, error: null }
    );
};

const handleCreateSuccess = (state, action) => {
    const { companyId } = action.meta || {};
    const consentState = resetCompanyConsents(state.expertConsent, companyId);
    const yearKey = String(action.payload.year);
    const byYear = {
        ...consentState.byYear,
        [yearKey]: [...(consentState.byYear[yearKey] || []), action.payload],
    };

    return updateOperationStatus(
        {
            ...state,
            expertConsent: {
                ...consentState,
                byYear,
            },
        },
        'create',
        { isLoading: false, error: null }
    );
};

const handleUpdateSuccess = (state, action) => {
    const { companyId, consentId } = action.meta || {};
    const consentState = resetCompanyConsents(state.expertConsent, companyId);
    const yearKey = String(action.payload.year);
    const rows = consentState.byYear[yearKey] || [];
    const byYear = {
        ...consentState.byYear,
        [yearKey]: [
            ...rows.map(row => (row.id === consentId ? { ...row, isActive: false } : row)),
            action.payload,
        ],
    };

    return updateOperationStatus(
        {
            ...state,
            expertConsent: {
                ...consentState,
                byYear,
            },
        },
        'update',
        { isLoading: false, error: null }
    );
};

const handleOperationError = (state, operation, action) =>
    updateOperationStatus(state, operation, {
        isLoading: false,
        error: extractErrorMessage(action.payload),
    });

const reducer = createReducer(INITIAL_STATE, {
    [acaActions.fetchExpertConsent.TRIGGER]: state =>
        updateOperationStatus(state, 'fetch', { isLoading: true, error: null }),
    [acaActions.fetchExpertConsent.SUCCESS]: handleFetchSuccess,
    [acaActions.fetchExpertConsent.ERROR]: (state, action) =>
        handleOperationError(state, 'fetch', action),
    [acaActions.createExpertConsent.TRIGGER]: state =>
        updateOperationStatus(state, 'create', { isLoading: true, error: null }),
    [acaActions.createExpertConsent.SUCCESS]: handleCreateSuccess,
    [acaActions.createExpertConsent.ERROR]: (state, action) =>
        handleOperationError(state, 'create', action),
    [acaActions.updateExpertConsent.TRIGGER]: state =>
        updateOperationStatus(state, 'update', { isLoading: true, error: null }),
    [acaActions.updateExpertConsent.SUCCESS]: handleUpdateSuccess,
    [acaActions.updateExpertConsent.ERROR]: (state, action) =>
        handleOperationError(state, 'update', action),
});

export default reducer;

/**
 * Get the expert consent state.
 *
 * @param  {Object} state The Redux state
 * @return {Object}
 */
export const getExpertConsentState = state =>
    get(state, 'acaReporting.expertConsent', INITIAL_STATE.expertConsent);

/**
 * Get all Expert Service consents for a year.
 *
 * @param  {Object} state The Redux state
 * @param  {number|string} year The consent year
 * @return {Array}
 */
export const getExpertConsentsByYear = (state, year) => {
    const rows = getExpertConsentState(state).byYear[String(year)];
    return Array.isArray(rows) ? rows : [];
};

/**
 * Get the latest active Expert Service opt-in consent for a year.
 *
 * @param  {Object} state The Redux state
 * @param  {number|string} year The consent year
 * @return {Object|null}
 */
export const getActiveExpertConsent = (state, year) =>
    getExpertConsentsByYear(state, year)
        .filter(row => row.consentStatus === 'opt_in' && row.isActive !== false)
        .pop() || null;

/**
 * Check whether a year has any prior Expert Service consent.
 *
 * @param  {Object} state The Redux state
 * @param  {number|string} year The consent year
 * @return {boolean}
 */
export const hasPriorExpertConsent = (state, year) =>
    getExpertConsentsByYear(state, year).length > 0;

/**
 * Get the status for an Expert Service consent operation.
 *
 * @param  {Object} state The Redux state
 * @param  {string} op The operation name
 * @return {Object}
 */
export const getExpertConsentStatus = (state, op) =>
    getExpertConsentState(state).status[op] || createOperationStatus();
